/* HOYA "האם התכוונתם ל…" for the cart drawer's city field.
   When the zone calculator does not recognise a typed city, rank() finds the Israeli localities spelled closest to it
   in assets/hoya-cities.json (names only, built by scripts/build-hoya-cities.mjs), and suggestVerified() keeps only the
   names the calculator itself recognises. Nothing here prices anything: every delivery answer still comes from the
   calculator. Also loads in Node (module.exports) for the tests and the list's build script. */
(() => {
  if (typeof window !== 'undefined' && window.HoyaCityMatch) return;

  const FINALS = { 'ך': 'כ', 'ם': 'מ', 'ן': 'נ', 'ף': 'פ', 'ץ': 'צ' };
  // Letters a shopper easily mixes up cost 0.4 instead of 1: sound-alike pairs, then neighbours on the Hebrew keyboard
  // (final forms are already folded, so מ/פ stands for the neighbouring keys ם and פ).
  const SOUND_ALIKE = ['כק', 'תט', 'סש', 'אע', 'אה', 'הע', 'חכ', 'בו', 'צז', 'גק'];
  const KEYBOARD = ['קר', 'רא', 'אט', 'טו', 'ונ', 'נמ', 'מפ', 'שד', 'דג', 'גכ', 'כע', 'עי', 'יח', 'חל', 'לכ', 'זס', 'סב', 'בה', 'הנ', 'מצ', 'צת'];
  // The same pairs as a table over the letters א…ת (U+05D0…U+05EA), read once per letter pair while comparing.
  const ALEF = 0x5d0;
  const SPAN = 27;
  const CHEAP = new Uint8Array(SPAN * SPAN);
  for (const [a, b] of [...SOUND_ALIKE, ...KEYBOARD]) {
    const x = a.charCodeAt(0) - ALEF;
    const y = b.charCodeAt(0) - ALEF;
    CHEAP[x * SPAN + y] = CHEAP[y * SPAN + x] = 1;
  }
  const CHEAP_COST = 0.4;
  // א, ה and ע are often added or dropped in spelling; adding or dropping one costs 0.6. Swapping two neighbours too.
  const SILENT = new Set(['א', 'ה', 'ע'].map(letter => letter.charCodeAt(0)));
  const SILENT_COST = 0.6;
  const SWAP_COST = 0.6;
  // "לחיפה", "בבת ים": a one-letter prefix is cut only when the whole input finds nothing, and it costs 0.5. Before
  // any guessing, the input without its prefix is also read against the short forms of 4 letters or more ("ליהוד",
  // "לראשון"), so a two-letter one such as "תא" never turns "בתא" into Tel Aviv.
  const PREFIXES = new Set(['ב', 'ל', 'מ', 'ו', 'ה']);
  const PREFIX_COST = 0.5;
  const PREFIXED_ALIAS_LETTERS = 4;
  // English matches by consonants only when both sides have at least 3 of them: with 2, "elad" (l, d) was LOD.
  const ENGLISH_CONSONANTS = 3;
  // Score = 1 − distance ÷ the longer form. A common city ranks 0.08 higher and needs less confidence than a small
  // place; a runner-up within 0.03 of the best is offered too. Values from the 2026-10-17 spike, checked by the spec's
  // test list and random-typo and random-letter tests (tests/hoya-city-match.test.cjs).
  const COMMON_BONUS = 0.08;
  const FLOOR = { hebrew: { common: 0.65, other: 0.82 }, english: { common: 0.7, other: 0.87 } };
  const RUNNER_UP = 0.03;
  const EPSILON = 1e-9;
  // A list request still running after 8 s is dropped, and the next check asks again.
  const LIST_TIMEOUT = 8000;
  const lists = new Map();
  const indexes = new WeakMap();

  // The form every comparison starts from: no niqqud, quotes or invisible direction marks, punctuation as spaces,
  // final letters as regular ones, Latin in lowercase.
  function normalize(text) {
    return String(text ?? '')
      .normalize('NFC')
      .replace(/[\u0591-\u05C7]|\p{Cf}/gu, '')
      .replace(/["'״׳`”“‘’]/g, '')
      .replace(/[-–—_.,()\/]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()
      .replace(/[ךםןףץ]/g, letter => FINALS[letter]);
  }

  // Hebrew spellings differ most in ו and י (קריית/קרית) and in a final א/ה (חיפא/חיפה), so names are compared
  // without ו and י, with a final א read as ה, spaces ignored.
  const hebrewSkeleton = text => text.replace(/[ וי]/g, '').replace(/א$/, 'ה');

  // English spellings of Hebrew names differ in how a sound is written (petah/petach, tiqwa/tikva) and in vowels:
  // both a folded form and a consonants-only form are kept, and the closer one counts.
  function englishForms(text) {
    const sounds = text.replace(/ /g, '')
      .replace(/ph/g, 'f').replace(/ch|kh/g, 'h').replace(/tz|ts/g, 'z')
      .replace(/q/g, 'k').replace(/c/g, 'k').replace(/w/g, 'v').replace(/b/g, 'v');
    return {
      fold: sounds.replace(/y/g, 'i').replace(/ei/g, 'e').replace(/(.)\1+/g, '$1'),
      consonants: sounds.replace(/[aeiouy]/g, '').replace(/(.)\1+/g, '$1')
    };
  }

  const letters = (text, pattern) => (text.match(pattern) || []).length;
  const insertCost = code => (SILENT.has(code) ? SILENT_COST : 1);
  function changeCost(a, b) {
    if (a === b) return 0;
    const x = a - ALEF;
    const y = b - ALEF;
    return x >= 0 && x < SPAN && y >= 0 && y < SPAN && CHEAP[x * SPAN + y] ? CHEAP_COST : 1;
  }

  // Weighted Damerau-Levenshtein distance (each letter edited at most once), on three rows reused between calls.
  let rows = [new Float64Array(32), new Float64Array(32), new Float64Array(32)];
  function distance(a, b) {
    if (rows[0].length <= b.length) rows = rows.map(() => new Float64Array(b.length + 1));
    let [beforePrevious, previous, current] = rows;
    previous[0] = 0;
    for (let j = 1; j <= b.length; j++) previous[j] = previous[j - 1] + insertCost(b.charCodeAt(j - 1));
    for (let i = 1; i <= a.length; i++) {
      const letter = a.charCodeAt(i - 1);
      const remove = insertCost(letter);
      current[0] = previous[0] + remove;
      for (let j = 1; j <= b.length; j++) {
        const other = b.charCodeAt(j - 1);
        let cost = Math.min(previous[j] + remove, current[j - 1] + insertCost(other), previous[j - 1] + changeCost(letter, other));
        if (i > 1 && j > 1 && letter === b.charCodeAt(j - 2) && a.charCodeAt(i - 2) === other) cost = Math.min(cost, beforePrevious[j - 2] + SWAP_COST);
        current[j] = cost;
      }
      const spare = beforePrevious;
      beforePrevious = previous;
      previous = current;
      current = spare;
    }
    return previous[b.length];
  }

  function similarity(input, name, penalty = 0) {
    const longer = Math.max(input.length, name.length);
    return longer ? 1 - (distance(input, name) + penalty) / longer : 0;
  }

  // Skeletons are computed once per list, not once per keystroke.
  function prepare(data) {
    const common = new Set(Array.isArray(data?.common) ? data.common : []);
    const aliases = new Map();
    for (const [key, name] of Object.entries(data?.aliases ?? {})) {
      if (typeof name !== 'string' || !name) continue;
      const normalized = normalize(key);
      for (const form of [normalized, normalized.replace(/ /g, '')]) if (form && !aliases.has(form)) aliases.set(form, name);
    }
    const cities = [];
    const seen = new Set();
    for (const entry of Array.isArray(data?.cities) ? data.cities : []) {
      const [he, en] = Array.isArray(entry) ? entry : [];
      if (typeof he !== 'string' || !he || seen.has(he)) continue;
      seen.add(he);
      const hebrew = normalize(he);
      const english = typeof en === 'string' ? normalize(en) : '';
      const { fold, consonants } = english ? englishForms(english) : { fold: '', consonants: '' };
      cities.push({
        name: he,
        common: common.has(he),
        skeleton: hebrewSkeleton(hebrew),
        words: hebrew.split(' ').length,
        fold,
        consonants,
        englishWords: english ? english.split(' ').length : 0
      });
    }
    return { cities, aliases };
  }

  // Best first: higher score, then the same number of words as typed, then a common city, then the shorter name
  // (then alphabetical, so the order never depends on the list's order).
  const order = (a, b) => (Math.abs(b.score - a.score) > EPSILON ? b.score - a.score : 0)
    || (b.sameWords - a.sameWords) || (b.common - a.common) || (a.name.length - b.name.length)
    || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0);

  // The best place and those nearly as close, best first. raw(city) scores a city (null skips it); wordsOf(city)
  // counts its words in the language typed.
  function leaders(index, text, raw, wordsOf) {
    const words = text.split(' ').length;
    const scored = [];
    let top = -Infinity;
    for (const city of index.cities) {
      const value = raw(city);
      if (value === null) continue;
      const score = value + (city.common ? COMMON_BONUS : 0);
      if (score > top) top = score;
      scored.push({ name: city.name, raw: value, score, common: city.common, sameWords: wordsOf(city) === words });
    }
    return scored.filter(city => top - city.score <= RUNNER_UP + EPSILON).sort(order);
  }

  // A suggestion needs confidence: the best place must clear its floor, and so must each runner-up offered with it.
  function shortlist(found, floorOf, limit) {
    const [best] = found;
    if (!best || best.raw + EPSILON < floorOf(best)) return [];
    return found.filter(city => city.raw + EPSILON >= floorOf(city)).slice(0, limit).map(city => city.name);
  }

  const hebrewFloor = city => (city.common ? FLOOR.hebrew.common : FLOOR.hebrew.other);
  const englishFloor = city => (city.common ? FLOOR.english.common : FLOOR.english.other);

  // Hebrew inputs of 3 letters or fewer only match aliases.
  function rankHebrew(index, text, limit) {
    if (letters(text, /[א-תa-z]/g) <= 3) return [];
    const scored = (form, penalty) => {
      const skeleton = hebrewSkeleton(form);
      return leaders(index, form, city => similarity(skeleton, city.skeleton, penalty), city => city.words);
    };
    const whole = shortlist(scored(text, 0), hebrewFloor, limit);
    if (whole.length || !PREFIXES.has(text[0])) return whole;
    return shortlist(scored(text.slice(1).trim(), PREFIX_COST), hebrewFloor, limit);
  }

  // English inputs under 4 folded letters only match aliases.
  function rankEnglish(index, text, limit) {
    const { fold, consonants } = englishForms(text);
    if (letters(fold, /[a-z]/g) < 4) return [];
    const byConsonants = consonants.length >= ENGLISH_CONSONANTS;
    const raw = city => (city.fold
      ? Math.max(similarity(fold, city.fold), byConsonants && city.consonants.length >= ENGLISH_CONSONANTS ? similarity(consonants, city.consonants) : 0)
      : null);
    return shortlist(leaders(index, text, raw, city => city.englishWords), englishFloor, limit);
  }

  // A short form, from the whole input or (4 letters or more) after a one-letter prefix.
  function aliasOf(index, text) {
    const alias = index.aliases.get(text) || index.aliases.get(text.replace(/ /g, ''));
    if (alias || !PREFIXES.has(text[0])) return alias;
    const rest = text.slice(1).trim().replace(/ /g, '');
    return rest.length >= PREFIXED_ALIAS_LETTERS ? index.aliases.get(rest) : undefined;
  }

  // Official Hebrew names for a typed city, best first, 0 to `limit` of them. Gibberish gets none.
  function rank(input, index, { limit = 2 } = {}) {
    const max = Math.floor(Number(limit));
    const text = normalize(input);
    if (!text || !index?.cities || !(max >= 1)) return [];
    const alias = aliasOf(index, text);
    if (alias) return [alias];
    return /[א-ת]/.test(text) ? rankHebrew(index, text, max) : rankEnglish(index, text, max);
  }

  async function fetchCities(url) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), LIST_TIMEOUT);
    try {
      const response = await fetch(url, { credentials: 'omit', signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP_${response.status}`);
      const data = await response.json();
      if (!Array.isArray(data?.cities)) throw new Error('CITIES_UNAVAILABLE');
      return data;
    } finally {
      clearTimeout(timeout);
    }
  }

  // One request per list URL. A failed or stalled request is forgotten, so the next check asks again.
  function loadCities(url) {
    const key = String(url ?? '');
    if (!key) return Promise.reject(new Error('CITIES_URL_MISSING'));
    if (!lists.has(key)) {
      const request = fetchCities(key).catch(error => {
        if (lists.get(key) === request) lists.delete(key);
        throw error;
      });
      lists.set(key, request);
    }
    return lists.get(key);
  }

  // Suggestions the calculator recognises: up to `maxChecks` ranked names are looked up in order, served or closed
  // ones are kept, up to `limit`. Only an unknown name moves on to the next one: an error answer means the calculator
  // is unreachable, so there is no suggestion at all, and neither is there after any failure (list or lookup).
  // The shopper is never blocked either way.
  async function suggestVerified(input, { citiesUrl, lookup, source, signal, timeout = 5000, maxChecks = 3, limit = 2 } = {}) {
    try {
      const data = await loadCities(citiesUrl);
      let index = indexes.get(data);
      if (!index) indexes.set(data, (index = prepare(data)));
      const found = [];
      for (const name of rank(input, index, { limit: maxChecks })) {
        if (found.length >= limit) break;
        const result = await lookup(name, { source, signal, timeout });
        const state = result?.state;
        if (state === 'served' || state === 'closed') found.push({ name, result });
        else if (state !== 'unknown') return [];
      }
      return found;
    } catch (_) {
      return [];
    }
  }

  const api = { normalize, prepare, rank, loadCities, suggestVerified };
  if (typeof window !== 'undefined') window.HoyaCityMatch = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
